import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

type Tenant = { id: string; name: string; deleted_at?: string | null };
let _tenantsCache: Tenant[] | null = null;

// Module-level filter state so every list page sees the same value —
// selection persists as the user navigates between Staff / People / CRM /
// Roles / Trash without re-typing. Not persisted across full page reloads,
// which matches "səhifə daxilində qorunsun" from the brief.
let _filterTenantId = "";
let _filterListeners: Array<(v: string) => void> = [];

/**
 * Hook + inline components for the platform-owner cross-tenant view.
 *
 * `isPlatformView` is true when the caller is a platform owner and has
 * NOT switched into a specific tenant (i.e. the "Platforma" context).
 * In that mode list pages surface a global banner and a per-row
 * "Manage" button that switches into the row's tenant.
 */
export function usePlatformView() {
    const { me, activeTenantId, switchTenant } = useAuth();
    const nav = useNavigate();
    const isPlatformView = !!me?.user?.is_platform_owner && !activeTenantId;
    const [tenants, setTenants] = useState<Tenant[]>(_tenantsCache || []);
    const [filterTenantId, setFilterLocal] = useState<string>(_filterTenantId);
    useEffect(() => {
        _filterListeners.push(setFilterLocal);
        return () => {
            _filterListeners = _filterListeners.filter((f) => f !== setFilterLocal);
        };
    }, []);
    const setFilterTenantId = (v: string) => {
        _filterTenantId = v;
        _filterListeners.forEach((f) => f(v));
    };
    useEffect(() => {
        if (isPlatformView && !_tenantsCache) {
            api.get("/platform/tenants", { params: { include_deleted: true } })
                .then((r) => {
                    const items = (r.data?.items || r.data || []) as Tenant[];
                    _tenantsCache = items;
                    setTenants(items);
                })
                .catch(() => { _tenantsCache = []; });
        }
    }, [isPlatformView]);
    const tenantName = useMemo(() => (id?: string | null) => {
        if (!id) return "—";
        return tenants.find((x) => x.id === id)?.name || id.slice(0, 8);
    }, [tenants]);
    const isTenantDeleted = (id?: string | null) => !!tenants.find((x) => x.id === id)?.deleted_at;
    const liveTenants = useMemo(() => tenants.filter((x) => !x.deleted_at), [tenants]);
    const manage = async (tenantId: string, nextPath?: string): Promise<boolean> => {
        try {
            await switchTenant(tenantId);
        } catch (e: any) {
            toast.error(`${i18n.t("no_tenant.switch_failed")} ${formatApiError(e)}`);
            return false;
        }
        if (nextPath) nav(nextPath);
        else nav(0 as any);
        return true;
    };
    return { isPlatformView, tenants: liveTenants, tenantName, isTenantDeleted, manage, filterTenantId, setFilterTenantId };
}

export function PlatformViewBanner() {
    const { isPlatformView, filterTenantId, setFilterTenantId } = usePlatformView();
    const { t } = useTranslation();
    if (!isPlatformView) return null;
    return (
        <div data-testid="platform-view-banner" className="rounded-md p-3 text-sm flex flex-wrap items-center gap-3"
             style={{ background: "var(--info-bg)", color: "var(--info-fg)", borderLeft: "3px solid var(--info-fg)" }}>
            <div className="flex items-start gap-2 flex-1 min-w-[200px]">
                <Building2 size={16} className="mt-0.5 shrink-0" />
                <div>{t("no_tenant.platform_hint")}</div>
            </div>
            <TenantFilter value={filterTenantId} onChange={setFilterTenantId} />
        </div>
    );
}

export function ManageTenantBtn({ tenantId, testId, nextPath }:
    { tenantId?: string | null; testId?: string; nextPath?: string }) {
    const { isPlatformView, tenantName, isTenantDeleted, manage } = usePlatformView();
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    if (!isPlatformView) return null;
    if (!tenantId) return <span className="text-xs" style={{ color: "var(--text-muted)" }}>—</span>;
    const deleted = isTenantDeleted(tenantId);
    return (
        <button data-testid={testId || `manage-tenant-${tenantId}`}
                disabled={deleted || busy}
                title={deleted ? `${t("platform.tenant_deleted_badge")} · ${t("no_tenant.restore_first")}` : undefined}
                onClick={async (e) => {
                    e.stopPropagation();
                    setBusy(true);
                    if (!(await manage(tenantId, nextPath))) setBusy(false);
                }}
                className="text-xs rounded-md px-2 py-1 border inline-flex items-center gap-1 whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60"
                style={{ borderColor: "var(--border)", color: deleted ? "var(--text-muted)" : "var(--primary)", background: "var(--selected-bg)" }}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Building2 size={12} />}
            <span className="hidden sm:inline">{deleted ? t("platform.tenant_deleted_badge") : t("no_tenant.manage_row")} · </span>
            <span className="max-w-[10rem] truncate">{tenantName(tenantId)}</span>
        </button>
    );
}

/** Platform-only tenant picker used inside create-forms (e.g. role dialog). */
export function TenantPicker({ value, onChange, testId }:
    { value: string; onChange: (id: string) => void; testId?: string }) {
    const { isPlatformView, tenants } = usePlatformView();
    const { t } = useTranslation();
    if (!isPlatformView) return null;
    return (
        <label className="block" data-testid={testId || "tenant-picker"}>
            <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("platform_view.pick_tenant")}</div>
            <select value={value} onChange={(e) => onChange(e.target.value)}
                    className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }}
                    data-testid="tenant-picker-select">
                <option value="">{t("platform_view.pick_tenant_placeholder")}</option>
                {tenants.map((tn) => <option key={tn.id} value={tn.id}>{tn.name}</option>)}
            </select>
        </label>
    );
}

/** Toolbar dropdown that filters a list by tenant. Renders null off-mode. */
export function TenantFilter({ value, onChange, testId }:
    { value: string; onChange: (v: string) => void; testId?: string }) {
    const { isPlatformView, tenants } = usePlatformView();
    const { t } = useTranslation();
    if (!isPlatformView) return null;
    return (
        <select data-testid={testId || "tenant-filter"} value={value}
                onChange={(e) => onChange(e.target.value)}
                className="surface-input px-3 py-2 text-sm rounded-md"
                style={{ minHeight: 40, minWidth: 180 }}>
            <option value="">{t("platform_view.filter_all")}</option>
            {tenants.map((tn) => <option key={tn.id} value={tn.id}>{tn.name}</option>)}
        </select>
    );
}

/** `<th>` header for the "Müəssisə" column. Renders null off-mode. */
export function TenantColHeader() {
    const { isPlatformView } = usePlatformView();
    const { t } = useTranslation();
    if (!isPlatformView) return null;
    return (
        <th data-testid="tenant-column" className="px-4 py-2 font-medium">
            {t("platform_view.institution_col")}
        </th>
    );
}

/** `<td>` cell that renders the row's tenant name. Renders null off-mode. */
export function TenantColCell({ tenantId, testId }:
    { tenantId?: string | null; testId?: string }) {
    const { isPlatformView, tenantName } = usePlatformView();
    if (!isPlatformView) return null;
    return (
        <td data-testid={testId || "tenant-cell"} className="px-4 py-3 text-xs whitespace-nowrap"
            style={{ color: "var(--text-muted)" }}>
            {tenantName(tenantId)}
        </td>
    );
}

/** Client-side helper: filter a list by an optional tenant id. */
export function applyTenantFilter<T extends { tenant_id?: string | null }>(
    rows: T[], filterTenantId: string,
): T[] {
    if (!filterTenantId) return rows;
    return rows.filter((r) => r.tenant_id === filterTenantId);
}
